
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import FilterSearch from "../../../../../components/FilterSearch(name)";
import Table from "../../../../../components/Table";
import Pagination from "../../../../../components/Pagination";
import Button from "../../../../../components/Button";
import CustomerFamilyModal from "../../../common/components/CustomerFamilyModal";
import { getCreditAccountCustomers } from "../../../../../api/frontShop/DebtorsAPI.js";
import styles from "../../css/CreditAccounts/CreditAccounts.module.css";

export default function CreditAccounts() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [familyOpen, setFamilyOpen] = useState(false);
  const [familyRows, setFamilyRows] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await getCreditAccountCustomers({
          search: search || "",
          page: currentPage,
          pageSize,
        });
        const json = res?.data;
        if (cancelled) return;
        setRows(Array.isArray(json?.data) ? json.data : []);
        setTotal(Number(json?.total || 0));
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [search, currentPage]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  const columns = useMemo(
    () => [
      { header: "Profile Number", accessor: "id" },
      { header: "Surname", accessor: "surname" },
      { header: "Initial", accessor: "initial" },
      { header: "Title", accessor: "title" },
      { header: "Address", accessor: "address" },
      {
        header: "Action",
        accessor: "action",
        render: (_value, row) => (
          <Button
            variant="primary"
            className={styles.actionBtn}
            onClick={() => {
              openFamilyFor(row);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  async function openFamilyFor(main) {
    setSelectedCustomer(main);
    setFamilyRows([]);
    setFamilyOpen(true);
    try {
      const res = await fetch(`/frontshop/checkout/customer/${encodeURIComponent(main.id)}/details`);
      if (!res.ok) throw new Error("Failed to load customer details");
      const json = await res.json();
      const list = [];
      if (json?.main) {
        list.push({
          patientid: json.main.patientid,
          surname: json.main.surname || "",
          initial: (json.main.firstname || "").slice(0, 1).toUpperCase(),
          title: json.main.title || "",
          role: "Main Member",
        });
      }
      const ben = Array.isArray(json?.beneficiaries) ? json.beneficiaries : [];
      for (const b of ben) {
        list.push({
          patientid: b.patientid,
          surname: b.surname || "",
          initial: (b.firstname || "").slice(0, 1).toUpperCase(),
          title: b.title || "",
          role: "Dependent",
          dependentcode: b.dependentcode || "",
        });
      }
      setFamilyRows(list);
    } catch {
      setFamilyRows([]);
    }
  }

  async function selectMember(member) {
    try {
      if (member?.patientid) {
        await fetch("/frontshop/checkout/customer/select", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ patientid: member.patientid }),
        });
      }
      const payload = {
        patientid: member.patientid,
        surname: member.surname,
        initial: member.initial,
        title: member.title,
        role: member.role,
      };
      try {
        localStorage.setItem("frontshop_customer", JSON.stringify(payload));
      } catch {}
    } finally {
      setFamilyOpen(false);
      navigate("/frontshop", { state: { selectedCustomer: true } });
    }
  }

  return (
    <div className={styles.wrapper}>
      <Navbar backTo="/frontshop/debtors" />

      <div className={styles.container}>
        <div className={styles.hint}>Select a customer to process a credit transaction</div>

        <div className={styles.toolbarRow}>
          <FilterSearch
            filterName="Filter"
            value={search}
            onChange={(v) => {
              setCurrentPage(1);
              setSearch(v);
            }}
            placeholder="Search"
            wrapperClassName={styles.filterSearch}
            labelClassName={styles.filterSearchLabel}
            searchContainerClassName={styles.filterSearchInputWrap}
            searchInputClassName={styles.filterSearchInput}
            iconColor="#A78BFA"
          />
        </div>

        <div className={styles.tableSection}>
          {error ? <div className={styles.error}>{error}</div> : null}
          {loading ? <div className={styles.loading}>Loading...</div> : null}
          <Table columns={columns} data={rows} />
        </div>

        <div className={styles.paginationContainer}>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {familyOpen && (
        <CustomerFamilyModal
          open={familyOpen}
          onClose={() => setFamilyOpen(false)}
          customer={selectedCustomer}
          rows={familyRows}
          onSelect={(member) => {
            selectMember(member);
          }}
          actionLabel="Select"
          title="Customer Details"
        />
      )}

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
